import { useState, useEffect } from 'react'
import { supabase, auth } from '../../lib/supabase'

export default function Usuario() {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    nombre: '',
    email: ''
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  useEffect(() => {
    fetchUserData()
  }, [])

  const fetchUserData = async () => {
    try {
      setLoading(true)
      
      // Obtener usuario autenticado
      const { session } = await auth.getSession()
      if (!session) return

      setUser(session.user)

      // Obtener datos del usuario de la tabla usuarios_marketing
      const { data, error } = await supabase
        .from('usuarios_marketing')
        .select('*')
        .eq('email', session.user.email)
        .single()

      if (error) throw error

      setUserData(data)
      setFormData({
        nombre: data.nombre || '',
        email: data.email || ''
      })
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const { error } = await supabase
        .from('usuarios_marketing')
        .update({
          nombre: formData.nombre
        })
        .eq('id', userData.id)

      if (error) throw error

      setMessage({ type: 'success', text: 'Perfil actualizado exitosamente' })
      fetchUserData()
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: 'Error al actualizar el perfil' })
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setMessage({ type: '', text: '' })

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' })
      return
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' })
      return
    }

    setSaving(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      })

      if (error) throw error

      setMessage({ type: 'success', text: 'Contraseña actualizada exitosamente' })
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: 'Error al cambiar la contraseña' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando perfil...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">MI PERFIL</h1>
        <p className="text-gray-600 mt-1">Administra tu información personal y configuración</p>
      </div>

      {/* Mensajes */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
          'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información del Perfil */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">INFORMACIÓN PERSONAL</h2>
            </div>
            <form onSubmit={handleUpdateProfile} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El email no se puede modificar
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>

          {/* Cambiar Contraseña */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">CAMBIAR CONTRASEÑA</h2>
            </div>
            <form onSubmit={handleChangePassword} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirma tu contraseña"
                  />
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {saving ? 'Actualizando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Columna Lateral - Info de Cuenta */}
        <div className="space-y-6">
          {/* Avatar y Info Básica */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-primary text-white flex items-center justify-center text-4xl font-bold mx-auto mb-4">
                {formData.nombre ? formData.nombre.charAt(0).toUpperCase() : 'U'}
              </div>
              <h3 className="font-bold text-lg">{formData.nombre}</h3>
              <p className="text-sm text-gray-600">{formData.email}</p>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-sm text-gray-600">Cuenta Activa</span>
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas de Usuario */}
          {userData && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-4">INFORMACIÓN DE LA CUENTA</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fecha de Registro:</span>
                  <span className="font-medium">
                    {new Date(userData.created_at).toLocaleDateString('es-PE')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rol:</span>
                  <span className="font-medium text-primary">Marketing</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Estado:</span>
                  <span className="font-medium text-green-600">
                    {userData.cuenta_activada ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Consejos de Seguridad */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-bold mb-3 text-blue-900">🔒 CONSEJOS DE SEGURIDAD</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Usa una contraseña segura y única</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Cambia tu contraseña regularmente</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>No compartas tus credenciales</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Cierra sesión al terminar</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

